/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useParams } from 'js/lib/useRouter';

import { Link } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import SkillCard from 'bundles/enterprise-legacy-learner-home/components/myLearning/skillsDashboard/SkillCard';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import SkillFocusTag from 'bundles/page-config-common/components/enterprise/SkillFocusTag';
import SkillsFocusModule from 'bundles/page-config-common/components/enterprise/SkillsFocusModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useEnterpriseSkills } from 'bundles/page-config-common/contexts/enterprise/EnterpriseSkillsContext';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { isSkillUpdatedHome } from 'bundles/skills-common/private/skillsInsightsEnabled';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { MY_LEARNING_QUERY_PARAM, QUERY_PARAM_VALUES } from 'bundles/unified-home-common/constants/MyLearningConstants';
import { mapSkillToOldSkillsProgressType } from 'bundles/unified-home-common/utils/SkillDashboardUtils';

import _t from 'i18n!nls/program-home';

const styles = {
  viewAllLink: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
  `,
  faqButtonIcon: css`
    margin-left: var(--cds-spacing-100);
  `,
};

const SkillFocusModuleRenderer = () => {
  const { areLatestSkillsLoading: loadingSkills, latestSkillsWithProgress = [] } = useEnterpriseSkills();
  const { programSlug } = useParams();
  const features = useEnterpriseProductFeatures();
  const thirdPartyOrg = useThirdPartyOrganization();

  const skills = latestSkillsWithProgress.map((skill) => mapSkillToOldSkillsProgressType(skill));

  const enableEnterpriseHomeRefresh = GrowthDiscoveryEpicClient.preview('enableEnterpriseHomeRefresh');

  if (!loadingSkills && !skills.length) {
    return null;
  }

  return (
    <HomeSection className="PageConfig_SkillsFocusComponent">
      {enableEnterpriseHomeRefresh ? (
        <SkillsFocusModule
          loading={!!loadingSkills}
          title={_t('Skills selected by #{orgName}', { orgName: thirdPartyOrg?.name })}
          skillCards={skills.map((skill, order) => (
            <SkillFocusTag
              key={skill.skillId}
              skill={skill}
              index={order}
              programSlug={programSlug}
              source="program-home"
            />
          ))}
        />
      ) : (
        <SkillsFocusModule
          loading={!!loadingSkills}
          title={_t('Narrow your focus with skills')}
          subtitle={_t('Build deep proficiency with expertly matched recommendations.')}
          cta={
            features?.enableSkillsDashboard ? (
              <TrackedDiv
                trackingName="program_home_skill_view_all"
                withVisibilityTracking
                requireFullyVisible={true}
                trackClicks={false}
              >
                <Link
                  component={TrackedLink2}
                  css={styles.viewAllLink}
                  typographyVariant="subtitleMedium"
                  trackingName="view_all_skills"
                  variant="quiet"
                  href={`/programs/${programSlug}/my-learning?${MY_LEARNING_QUERY_PARAM}=${QUERY_PARAM_VALUES.SKILLS_DASHBOARD}`}
                  data-testid="view-all-skills-link"
                >
                  {_t('View all Skills')}
                  <ArrowNextIcon />
                </Link>
              </TrackedDiv>
            ) : null
          }
          skillCards={skills.map((skill, order) => (
            <SkillCard
              hideLinks={isSkillUpdatedHome()}
              source="program-home"
              trackingName="program_home_skill_card"
              key={skill.skillId}
              size="small"
              skill={skill}
              programSlug={programSlug}
              trackingData={{
                skill,
                order,
                pageSection: {
                  sectionName: 'cta_banner',
                  index: 2,
                },
              }}
            />
          ))}
        />
      )}
    </HomeSection>
  );
};

export default SkillFocusModuleRenderer;
